import React, { useContext } from 'react';
import DealContext from './DealContext';
import DbEditContainer from '../db/DbEditContainer';
import { Tab, Badge, Col, Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import DealInformation from './DealInformation';
import ListEditContainer from '../list/ListEditContainer';
import CalculationSummary from './CalculationSummary';
import QuoteDocuments from './QuoteDocuments';
import QuoteHistory from './QuoteHistory';
import FileNotFound from '../FileNotFound';
import ViewNotes from './ViewNotes';
import { modal } from '../modal/ModalEmitter';

const QuoteTabs = ({ activeTabIndex, changeTab, showCustomer, closeCustomer }) => {
  const { user } = useSelector(state => state.app);
  const { deal, openQuotes, quoteList, roles, closeQuote, notes, fetchDealNotes, fetchDeal, quoteMeta, team } =
    useContext(DealContext);

  const hasSalesRole = roles.isSalesPerson || roles.isSalesManager || roles.isSalesAccountant;

  const hasAccess = quoteId => {
    // Manager and Account can see (Account can't save though)
    if (roles.isSalesManager || roles.isSalesAccountant) {
      return true;
    }

    // If not a salesperson at all is a fail
    if (!roles.isSalesPerson) {
      return false;
    }

    // Salesperson must also be assigned to Quote
    const quote = quoteList.find(q => q.id === quoteId);

    if (quote.assignedTo === user.id) {
      return true;
    }

    return false;
  };

  const isQuoteReadOnly = quote => {
    if (!hasSalesRole) {
      return true;
    }

    if (quote.QuoteState.alive === false) {
      return true;
    }

    if (!roles.isSalesManager && quote.QuoteState.readOnly === true) {
      return true;
    }

    return false;
  };

  const getCustomActions = quoteId => {
    const quoteNotes = notes.filter(n => n.itemId === String(quoteId));

    return [
      {
        key: 'calc',
        id: 'calc',
        label: <i className="fas fa-calculator fa-fw" />,
        side: 'right',
        title: 'Calculations',
        className: 'icon-only',
        action: ({ manager }) => {
          modal.open('calcExplain', { title: 'Calculations', message: <CalculationSummary manager={manager} /> });
        },
      },
      {
        key: 'audit',
        id: 'audit',
        label: <i className="fab fa-buffer fa-fw" />,
        side: 'right',
        title: 'History',
        className: 'icon-only',
        action: () => {
          modal.open('QuoteHistory', {
            title: `Quote History ${quoteId}`,
            size: 'lg',
            style: 'center',
            hidePrint: true,
            message: <QuoteHistory quoteId={Number(quoteId)} />,
          });
        },
      },
      {
        key: 'qdocs',
        label: <i className="fas fa-print fa-fw" />,
        side: 'right',
        id: 'qdocs',
        title: 'Documents',
        className: 'icon-only',
        action: () => {
          modal.open('QuoteDocuments', {
            title: `Quote Documents ${quoteId}`,
            size: 'md',
            style: 'center',
            hidePrint: true,
            message: <QuoteDocuments quoteId={Number(quoteId)} />,
          });
        },
      },
      {
        key: 'qnotes',
        label: (
          <span>
            <i className="fas fa-comment-alt fa-fw" />
            {quoteNotes.length !== 0 && (
              <Badge pill variant="primary">
                {quoteNotes.length}
              </Badge>
            )}
          </span>
        ),
        side: 'right',
        id: 'qnotes',
        title: 'Notes',
        className: 'icon-only',
        action: () => {
          modal.open('viewNotes', {
            hideFooter: true,
            hidePrint: true,
            size: 'md',
            style: 'center',
            title: `View Notes ${quoteId}`,
            message: <ViewNotes dealId={deal.id} listItem={quoteId} listName="EQS1" callback={fetchDealNotes} />,
          });
        },
      },
    ];
  };

  const quoteSubmitCallback = quoteId => {
    handleQuoteUpdate(quoteId);
  };

  const handleQuoteUpdate = () => {
    fetchDeal();
  };

  const handleFieldBlur = () => {};

  const handleFieldChange = ({ name, dataIndex, rowId, updateForm, value }) => {
    // Fill in default values for Costs, Add-ons, and Misc Fees
    if (name === 'data.costName') {
      if (quoteMeta.costs) {
        const record = quoteMeta.costs.find(c => c.id === Number(value));
        if (record && record.data.defaultValue) {
          updateForm({ 'data.costCost': record.data.defaultValue }, dataIndex, rowId);
        }
      }
    } else if (name === 'data.feeDescription') {
      if (quoteMeta.fees) {
        const record = quoteMeta.fees.find(c => c.id === Number(value));
        if (record) {
          const payload = {
            'data.feeTaxable': record.data.defaultTaxable || 'N',
          };

          if (record.data.defaultValue) {
            payload['data.feeCost'] = record.data.defaultValue;
          }

          updateForm(payload, dataIndex, rowId);
        }
      }
    } else if (name === 'data.addonDesc') {
      if (quoteMeta.addons) {
        const record = quoteMeta.addons.find(c => c.id === Number(value));
        if (record) {
          const payload = {
            'data.addonPrint': record.data.defaultPrint || 'N',
            'data.addonFET': record.data.defaultFET || 'N',
            'data.addonTaxable': record.data.defaultTaxable || 'N',
            'data.addonAddToCost': record.data.defaultAddToCost || 'N',
          };

          if (record.data.defaultCost) {
            payload['data.addonCost'] = record.data.defaultCost;
          }

          if (record.data.defaultPrice) {
            payload['data.addonPrice'] = record.data.defaultPrice;
          }

          updateForm(payload, dataIndex, rowId);
        }
      }
    }
  };

  const tabs = [];
  const content = [];

  tabs.push(
    <Nav.Item key="home">
      <Nav.Link eventKey="home">
        {deal.name && !/^Deal For Q\d+/.test(deal.name) ? deal.name : 'Deal Information'}
      </Nav.Link>
    </Nav.Item>
  );

  content.push(
    <Tab.Pane key="home" eventKey="home">
      <Col style={{ backgroundColor: '#fafafa' }}>{deal.id && <DealInformation />}</Col>
    </Tab.Pane>
  );

  if (showCustomer && deal.Customer) {
    tabs.push(
      <Nav.Item key="customer">
        <Nav.Link eventKey="customer">
          <div className="deal-tab">
            <div>
              <i className="fa fa-id-card mr-1" /> {deal.Customer.fullName}
            </div>
            <div
              className="tab-button"
              title="Hide Customer From View"
              onClick={event => {
                event.stopPropagation();
                closeCustomer();
              }}
            >
              <i className="fal fa-times" />
            </div>
          </div>
        </Nav.Link>
      </Nav.Item>
    );

    content.push(
      <Tab.Pane key="customer" eventKey="customer">
        <ListEditContainer
          key={`cust-${deal.customerId}`}
          action="edit"
          itemId={deal.customerId}
          itemName="EQS5"
          handleCancel={closeCustomer}
          submitCallback={() => {
            fetchDeal({ forceUpdate: true });
          }}
          persistTabs={false}
          usePrefix="cus-"
        />
      </Tab.Pane>
    );
  }

  openQuotes.forEach(quoteId => {
    const quote = quoteList.find(q => q.id === quoteId);
    if (!quote) {
      return null;
    }

    const isReadOnly = isQuoteReadOnly(quote);
    const hasVehicle = quote && quote.vehicleId;
    const tabLabel = hasVehicle ? (
      <>
        <span className="tl-1">{`Q${quoteId}`}</span>
        <span className="tl-2">
          <i className="fas fa-angle-double-right fa-xs ml-1 mr-1" /> {quote.vehicleId}
        </span>
      </>
    ) : (
      `Quote ${quoteId}`
    );
    const statusColor = quote.QuoteState ? quote.QuoteState.color : '#fff';

    const title = (
      <div className="deal-tab" title={hasVehicle ? quote.vehicleId : `Quote ${quoteId}`}>
        {quote.QuoteState && (
          <div className="tab-label">
            <i
              className="fa-solid fa-circle"
              style={{ color: statusColor, paddingRight: '3px' }}
              title={quote.QuoteState.name}
            />
            {tabLabel}
          </div>
        )}
        <div
          className="tab-button"
          title="Hide Quote From View"
          onClick={event => {
            event.stopPropagation();
            closeQuote(quoteId);
          }}
        >
          <i className="fal fa-times" />
        </div>
      </div>
    );

    tabs.push(
      <Nav.Item key={String(quoteId)}>
        <Nav.Link eventKey={quoteId}>{title}</Nav.Link>
      </Nav.Item>
    );

    content.push(
      <Tab.Pane key={String(quoteId)} eventKey={quoteId} className="quote-tab-content">
        <div className="page-container">
          {hasAccess(quoteId) ? (
            <DbEditContainer
              action="edit"
              cancelLabel="Close"
              customActions={getCustomActions(quoteId)}
              itemId={String(quoteId)}
              itemName="EQS1"
              key={`Q${quoteId}${quote.key || ''}`}
              namespace="db"
              onCancel={() => closeQuote(quoteId)}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              persistTabs={false}
              readOnly={isReadOnly}
              showHelp={false}
              submitCallback={(response, { changes }) => quoteSubmitCallback(quoteId, changes)}
              usePrefix={`q${quoteId}-`}
            />
          ) : (
            <FileNotFound icon="fas fa-exclamation-triangle">
              Need Sales Manager role or be Sales Person assigned to Quote
            </FileNotFound>
          )}
        </div>
      </Tab.Pane>
    );
  });

  return (
    <Tab.Container defaultActiveKey={activeTabIndex} activeKey={activeTabIndex} onSelect={changeTab} transition={false}>
      <Nav variant="tab" className="deal-tabs nav-tabs pl-3" style={{ marginRight: '50px' }}>
        {tabs}
      </Nav>
      <Tab.Content>{content}</Tab.Content>
    </Tab.Container>
  );
};

export default QuoteTabs;
